import React, { useEffect, useState } from 'react'

import { useDashboardFolder } from 'redux/hooks/DashboardFolders'
import { useDashboardFolderActions } from 'redux/actions/DashboardFolders'
import { useTranslation } from 'language/translator'

import Button from 'components_new/atoms/Button'
import TextField from 'components_new/atoms/TextField'

import OptionsMenu from 'components_new/molecules/OptionsMenu'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'

interface FolderOptionsMenuProps {
  folderId: string
  handleClose: () => void
  menuAnchorEl: HTMLAnchorElement | null
}

const FolderOptionsMenu = (props: FolderOptionsMenuProps) => {
  const { folderId, handleClose, menuAnchorEl } = props

  const folder = useDashboardFolder(folderId)

  const { tryDeleteDashboardFolder, tryUpdateDashboardFolder } =
    useDashboardFolderActions()

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)

  const [newTitle, setNewTitle] = useState<string>(folder?.name ?? '')
  const { t } = useTranslation()

  useEffect(() => {
    if (editOpen) setNewTitle(folder?.name ?? '')
  }, [editOpen])

  const handleCloseEdit = () => {
    setEditOpen(false)
    handleClose()
  }

  if (!folder) return null

  return (
    <>
      <OptionsMenu
        anchorEl={menuAnchorEl}
        disableScrollLock={true}
        onClose={handleClose}
        open={Boolean(menuAnchorEl)}
        options={[
          {
            divider: true,
            iconName: 'Edit',
            onClick: () => {
              handleClose()

              setEditOpen(true)
            },
            title: t('global', 'change_name')
          },
          {
            iconName: 'Delete',
            onClick: () => {
              handleClose()

              setDeleteOpen(true)
            },
            title: t('global', 'delete')
          }
        ]}
      />

      <DeleteDialog
        onDelete={(cb) => {
          tryDeleteDashboardFolder(folder.id)
          setDeleteOpen(false)
          handleClose()
          cb()
        }}
        onClose={() => setDeleteOpen(false)}
        open={deleteOpen}
        title={folder.name}
        type={t('global', 'folder')}
      />

      <SimpleDialog
        actions={
          <>
            <Button variant="text" onClick={handleCloseEdit}>
              {t('global', 'cancel')}
            </Button>
            <Button
              disabled={newTitle.length === 0}
              variant="contained"
              onClick={() => {
                tryUpdateDashboardFolder(folder.id, {
                  data: {
                    name: newTitle
                  }
                })

                setEditOpen(false)
              }}
            >
              {t('global', 'save')}
            </Button>
          </>
        }
        onClose={handleCloseEdit}
        open={editOpen}
        title={t('global', 'change_name')}
      >
        <TextField
          fullWidth={true}
          label={t('global', 'name')}
          autoComplete="off"
          value={newTitle}
          onChange={(event) => setNewTitle(event.target.value)}
          sx={{ mt: 1, mb: 2 }}
          required
        />
      </SimpleDialog>
    </>
  )
}

export default FolderOptionsMenu
